import { Form } from '@innovationdepartment/proxima-ui';
import type { FormApi } from 'final-form';
import { useShowSpinner } from 'hooks';
import ModalBase from 'ui/ModalBase';
import validateUrl from 'utils/validations/validateUrl';
import CreateBrandModalView from './CreateBrandModal.View';
import type { CreateBrandModalFormValues, CreateBrandModalProps } from './types';
import useCreateBrand, { type CreateBrandParams } from './useCreateBrand';

type BrandSetupKeys = 'name' | 'website' | 'category';
type ErrorDefinitions = { [key in BrandSetupKeys]?: string };
const URL_MAX_CHARACTERS_ALLOWED = 253;

const CreateBrandModal = (props: CreateBrandModalProps) => {
  const { onClose, open } = props;
  const { createBrand, loading } = useCreateBrand();

  useShowSpinner({ show: loading });

  const initialValues: CreateBrandModalFormValues = {
    category: undefined,
    name: '',
    website: ''
  };

  const validate = async (formData: typeof initialValues): Promise<ErrorDefinitions> => {
    const errors: { [key in BrandSetupKeys]?: string } = {};
    if (!formData.name?.length) errors.name = 'Required';
    if (!formData.category) errors.category = 'Required';
    if (validateUrl(formData.website ?? '')) errors.website = 'Invalid URL format';
    if (!formData.website?.length) errors.website = 'Required';
    if ((formData.website?.length ?? 0) > URL_MAX_CHARACTERS_ALLOWED) {
      errors.website = 'URL too long';
    }

    return errors;
  };

  const onSubmit = async (
    formData: typeof initialValues,
    formApi: FormApi<CreateBrandModalFormValues>
  ) => {
    try {
      await createBrand(formData as CreateBrandParams);
    } catch (e) {
      const errors: ErrorDefinitions = {};

      const { response } = e as {
        response?: { status: number; data: { path: string; message: string }[] };
      };
      if (response?.status === 409) {
        response.data.forEach(error => {
          if (error.path === 'name') {
            errors.name =
              'This brand already exists. Please request an invite from the account admin.';
          }
          if (error.path === 'domain') {
            errors.website = 'This URL is already taken.';
          }
        });
      }

      if (Object.entries(errors).length > 0) {
        return errors;
      }

      return undefined;
    }

    onClose(true); // signal to reload the brand list

    formApi.reset(); // reset the form after successful submission
    return undefined; // no errors
  };

  return (
    <ModalBase open={open} onClose={() => onClose()} title="Create Brand">
      <Form initialValues={initialValues} validate={validate} onSubmit={onSubmit}>
        <CreateBrandModalView loading={loading} />
      </Form>
    </ModalBase>
  );
};

export default CreateBrandModal;
